import { useCallback, useEffect, useRef, useState } from 'react';

// 重载签名：当提供 defaultValue 时，返回类型为 T
export function useControl<T>(
  defaultValue: T | (() => T),
  value?: T,
  onChange?: (value: T) => void
): [T, (value: T | ((prevValue: T) => T)) => void];

// 重载签名：当未提供 defaultValue 时，返回类型为 T | undefined
export function useControl<T>(
  defaultValue?: undefined,
  value?: T,
  onChange?: (value: T) => void
): [T | undefined, (value: T | ((prevValue: T | undefined) => T)) => void];

/**
 * 用于处理受控和非受控状态的 Hook
 *
 * @param defaultValue - 默认值（用于非受控模式）或返回默认值的函数
 * @param value - 受控值（如果提供则为受控模式）
 * @param onChange - 值变化回调
 * @returns [currentValue, setValue] - 当前值和设置值的函数
 */
export function useControl<T>(
  defaultValue?: T | (() => T),
  value?: T,
  onChange?: (value: T) => void
): [T | undefined, (value: T | ((prevValue: T | undefined) => T)) => void] {
  // 判断是否为受控模式
  const isControlled = value !== undefined;
  const prevIsControlled = useRef(isControlled);

  // 内部状态（仅在非受控模式下使用）
  const [internalValue, setInternalValue] = useState<T | undefined>(() => {
    const _default =
      typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
    return value ?? _default;
  });
  useEffect(() => {
    if (isControlled) {
      setInternalValue(value);
    } else if (prevIsControlled.current && !isControlled) {
      // 从受控模式切换到非受控模式，重置为默认值
      const _default =
        typeof defaultValue === 'function' ? (defaultValue as () => T)() : defaultValue;
      setInternalValue(_default);
    }
    prevIsControlled.current = isControlled;
  }, [isControlled, value, defaultValue]);

  // 当前值：受控模式使用传入的 value，非受控模式使用内部状态
  const currentValue = isControlled ? value : internalValue;

  // 设置值的函数
  const setValue = useCallback(
    (valueOrUpdater: T | ((prevValue: T | undefined) => T)) => {
      // 计算新值
      const newValue =
        typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prevValue: T | undefined) => T)(currentValue)
          : valueOrUpdater;

      // 不管是不是非受控模式，都需要更新内部状态
      setInternalValue(newValue);

      // 无论受控还是非受控，都调用 onChange
      onChange?.(newValue);
    },
    [onChange, currentValue]
  );

  return [currentValue, setValue];
}
